"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import {
  getStepProgressStatuses,
  type DiagnosisUnlockStepProgressStatus,
} from "./diagnosisUnlockFourStep.contract";

export type DiagnosisUnlockStepProgressProps = {
  /** 步骤文案；长度决定 progress 行数 */
  labels: readonly string[];
  /** 当前 active 步索引；`>= labels.length` 时全部为 completed */
  activeIndex: number;
  className?: string;
};

function StepIndicator({
  status,
}: {
  status: DiagnosisUnlockStepProgressStatus;
}) {
  if (status === "completed") {
    return (
      <div
        className="flex size-5 shrink-0 items-center justify-center rounded-[10px] bg-content_aura_lv2_primary"
        data-testid="diagnosis-unlock-step-progress-check"
      >
        <Icon name="check" size={16} className="text-content_inverse_primary" />
      </div>
    );
  }

  if (status === "active") {
    return (
      <Icon
        name="ic_loading2"
        size={20}
        className="shrink-0 animate-spin"
        color={ospThemes.colors.content_inverse_primary}
        data-testid="diagnosis-unlock-step-progress-spinner"
      />
    );
  }

  return (
    <div
      className="size-5 shrink-0 rounded-[10px] bg-background_inverse_overlay20"
      data-testid="diagnosis-unlock-step-progress-pending"
    />
  );
}

/**
 * Figma `90772:39836` Progress section — 可配置步骤列表。
 */
export function DiagnosisUnlockStepProgress({
  labels,
  activeIndex,
  className,
}: DiagnosisUnlockStepProgressProps) {
  const statuses = getStepProgressStatuses(labels, activeIndex);

  return (
    <div
      className={cn(
        "w-full rounded-xl bg-border_inverse_solid px-6 py-4 backdrop-blur-[10px]",
        className,
      )}
      data-testid="diagnosis-unlock-step-progress"
      data-figma-node-id="90772:39836"
    >
      <ul className="flex flex-col gap-3.5 pt-1">
        {labels.map((label, index) => {
          const status = statuses[index];
          const isActive = status === "active";
          const isPending = status === "pending";

          return (
            <li
              key={`${label}-${index}`}
              className="flex items-center gap-3"
              data-testid={`diagnosis-unlock-step-progress-row-${index}`}
            >
              <StepIndicator status={status} />
              <span
                className={cn(
                  "body_m flex-1 text-content_inverse_primary",
                  isActive && "body_medium_m",
                  isPending && "text-content_inverse_tertiary",
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** @deprecated 使用 {@link DiagnosisUnlockStepProgress} */
export const DiagnosisUnlockFourStepProgress = DiagnosisUnlockStepProgress;
